/**
 * Keeps Web Audio silent while the page is in the background — switching to
 * another app / tab, minimizing the browser or locking the phone. Without this
 * an AudioContext keeps playing (the music scheduler and oscillators don't care
 * that the page is hidden).
 *
 * The context is suspended when the page hides and resumed when it is shown
 * again, but only if this module suspended it (so a context the user never
 * started, or a muted player, is never started by coming back to the tab).
 */
export function bindAudioLifecycle(
  getContext: () => AudioContext | null,
  isActive: () => boolean,
): () => void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return () => {};

  let suspendedByUs = false;

  const goBackground = (): void => {
    const ctx = getContext();
    if (!ctx || typeof ctx.suspend !== 'function') return;
    const state = ctx.state as string;
    if (state === 'running') {
      suspendedByUs = true;
      void ctx.suspend();
    } else if (state === 'interrupted') {
      // iOS уже сам остановил звук (блокировка/звонок) — запомним, чтобы вернуть при показе
      suspendedByUs = true;
    }
  };

  const comeForeground = (): void => {
    const ctx = getContext();
    if (!ctx || !suspendedByUs) return;
    suspendedByUs = false;
    // iOS can also leave the context 'interrupted' after a lock / phone call.
    if (isActive() && ctx.state !== 'running') void Promise.resolve(ctx.resume()).catch(() => undefined);
  };

  const onVisibility = (): void => (document.hidden ? goBackground() : comeForeground());
  const onPageHide = (): void => goBackground();
  const onPageShow = (): void => {
    if (!document.hidden) comeForeground();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
  };
}
